export const MATERIAL_CATEGORIES = [
  '教材',
  '暑假作业',
  '最近试卷',
  '课堂例题',
  '日常作业',
  '课堂小测',
  '周测月考',
  '教师讲义',
] as const

export type MaterialCategory = (typeof MATERIAL_CATEGORIES)[number]

export interface MaterialRecord {
  id: string
  name: string
  size: number
  mimeType: string
  category: MaterialCategory
  createdAt: string
  blob: Blob
}

const DATABASE_NAME = 'studna-materials'
const STORE_NAME = 'materials'
const DATABASE_VERSION = 1

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION)

    request.onupgradeneeded = () => {
      const database = request.result
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: 'id' })
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('资料库打开失败'))
  })
}

export async function listMaterials(): Promise<MaterialRecord[]> {
  const database = await openDatabase()
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readonly')
    const request = transaction.objectStore(STORE_NAME).getAll()

    request.onsuccess = () => {
      const records = [...(request.result as MaterialRecord[])].sort((a, b) =>
        b.createdAt.localeCompare(a.createdAt),
      )
      resolve(records)
    }
    request.onerror = () => reject(request.error ?? new Error('资料读取失败'))
    transaction.oncomplete = () => database.close()
  })
}

export async function saveMaterial(
  file: File,
  category: MaterialCategory,
): Promise<MaterialRecord> {
  const record: MaterialRecord = {
    id: crypto.randomUUID(),
    name: file.name,
    size: file.size,
    mimeType: file.type || 'application/octet-stream',
    category,
    createdAt: new Date().toISOString(),
    blob: file,
  }
  const database = await openDatabase()

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite')
    transaction.objectStore(STORE_NAME).put(record)
    transaction.oncomplete = () => {
      database.close()
      resolve(record)
    }
    transaction.onerror = () => {
      database.close()
      reject(transaction.error ?? new Error('资料保存失败'))
    }
  })
}

export async function deleteMaterial(materialId: string): Promise<void> {
  const database = await openDatabase()
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite')
    transaction.objectStore(STORE_NAME).delete(materialId)
    transaction.oncomplete = () => {
      database.close()
      resolve()
    }
    transaction.onerror = () => {
      database.close()
      reject(transaction.error ?? new Error('资料删除失败'))
    }
  })
}

export function downloadMaterial(material: MaterialRecord) {
  const url = URL.createObjectURL(material.blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = material.name
  anchor.click()
  URL.revokeObjectURL(url)
}
